"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AccountScreen } from "@/components/account/account-screen";
import { ClaimToast } from "@/components/account/claim-toast";
import type { AuthTrigger } from "@/lib/analytics/events";

/**
 * Ouvre la modale de compte depuis n'importe où, en disant d'où vient la
 * demande.
 *
 * Un contexte plutôt qu'un état local parce que les points d'entrée sont
 * dispersés — le header, la carte de bienvenue, l'onglet « Mes morceaux », et
 * demain l'intercepteur de 401 — alors que la modale doit rester unique et
 * montée au-dessus de tout.
 *
 * Le `trigger` n'est pas décoratif : c'est lui qui, segmenté dans
 * `account_registered`, permet de trancher avec des chiffres lequel des
 * moments d'inscription convertit vraiment.
 */

type AccountModalState = {
  open: boolean;
  trigger: AuthTrigger;
};

/**
 * `openAccountModal` ne prend plus de mode : l ecran ne demande plus si la
 * personne est nouvelle, donc les points d entree n ont plus a deviner son
 * intention.
 */
type AccountModalContextValue = {
  openAccountModal: (trigger: AuthTrigger) => void;
  closeAccountModal: () => void;
  isAccountModalOpen: boolean;
};

const AccountModalContext = createContext<AccountModalContextValue | null>(null);

const CLOSED: AccountModalState = {
  open: false,
  trigger: "header",
};

export function AccountModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AccountModalState>(CLOSED);

  const openAccountModal = useCallback((trigger: AuthTrigger) => {
    setState({ open: true, trigger });
  }, []);

  const closeAccountModal = useCallback(() => {
    setState((previous) => ({ ...previous, open: false }));
  }, []);

  const value = useMemo(
    () => ({
      openAccountModal,
      closeAccountModal,
      isAccountModalOpen: state.open,
    }),
    [openAccountModal, closeAccountModal, state.open],
  );

  return (
    <AccountModalContext.Provider value={value}>
      {children}
      {/*
        Plus de conteneur d'empilement : l'écran de compte porte lui-même son
        `z-[90]`, au-dessus de `WelcomeStudioCard` (`z-[80]`) — sans quoi il se
        peindrait derrière elle quand on s'y connecte, c'est-à-dire nulle part.
      */}
      <AccountScreen
        open={state.open}
        onClose={closeAccountModal}
        trigger={state.trigger}
      />
      <ClaimToast />
    </AccountModalContext.Provider>
  );
}

export function useAccountModal() {
  const context = useContext(AccountModalContext);

  if (!context) {
    throw new Error("useAccountModal must be used inside an AccountModalProvider");
  }

  return context;
}
